/**
 * Showroom
 *
 * Main routed shell for the dealer experience. Owns all top-level routing and
 * wraps the matched route in the shared `PageLayout` shell. The layout variant
 * is route-driven (`useShowroomLayoutIdentifier`), so:
 * - `/showroom/*` renders the sidebar chrome around the build-your-offer content.
 * - `/manage-documents` renders the back-button chrome around the deal jacket.
 *
 * Each route carries the capability key gating it, so a hidden feature is
 * unreachable by any entry point — nav click, typed URL, back button or deep link.
 *
 * The route table itself is a function of the full-scroll toggle
 * (`buildShowroomRoutes`): the one-page address is always registered, and under the
 * toggle it renders the stacked page instead of the four standalone
 * Build-Your-Offer screens.
 */
import { Switch } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { CustomerCardPreloader, PageLayout } from '@makemydeal/dr-sales-checkin-components';
import { isFullPageScrollEnabled } from '@makemydeal/dr-sales-checkin-store';
import { useAppRouter } from '@makemydeal/dr-sales-checkin-ui-utils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { useShowroomLayoutIdentifier } from '../../hooks';
import { ShowroomCard, ShowroomBody } from './showroom.style';
import { buildShowroomRoutes } from './BuildShowroomRoutes';

const Showroom = () => {
    const { search } = useAppRouter();
    const variant = useShowroomLayoutIdentifier();
    const isFullScrollEnabled = useSelector(isFullPageScrollEnabled);
    const preloaderEnabled = useSelector(featureToggleSelectors.isSciCustomerCardPreloaderEnabled);

    return (
        <>
            {preloaderEnabled && <CustomerCardPreloader />}
            <PageLayout variant={variant}>
                <ShowroomBody data-testid="showroom-body">
                    <ShowroomCard>
                        <Switch>{buildShowroomRoutes(isFullScrollEnabled, search)}</Switch>
                    </ShowroomCard>
                </ShowroomBody>
            </PageLayout>
        </>
    );
};

export default Showroom;
